import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import io from "socket.io-client";
import dayjs from "dayjs";
import { Button, Spin, Tag } from "antd";
import { returnUpDownClassname } from "utils/helpers";
import { ago, money, percent, tradeLabel } from "./format";
import HistoryTab from "./HistoryTab";
import AutomationTab from "./AutomationTab";
import "./MiniApp.scss";

// Telegram Mini App (/tg, opened from the bot's "Positions" menu button), three
// tabs: live positions, trades history, automation controls. Signs in with
// Telegram's signed initData (no password); live rates are pushed over
// socket.io as AutoTrader posts them, the positions are re-read every 30s.
const TELEGRAM_SCRIPT = "https://telegram.org/js/telegram-web-app.js";
const REFRESH_MS = 30 * 1000;

const TABS = [
  { key: "positions", label: "Positions" },
  { key: "history", label: "History" },
  { key: "automation", label: "Automation" },
];

const loadTelegram = () =>
  new Promise((resolve) => {
    if (window.Telegram?.WebApp) return resolve(window.Telegram.WebApp);
    const script = document.createElement("script");
    script.src = TELEGRAM_SCRIPT;
    script.onload = () => resolve(window.Telegram?.WebApp || null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });

// P&L of the position at a price: % of the open price, $ for the whole position
const pnlAt = (position, rate) => {
  if (!(rate > 0) || !(position.openRate > 0)) return null;
  const multiplier = position.option?.multiplier || 1;
  return {
    perc: ((rate - position.openRate) / position.openRate) * 100,
    value: (rate - position.openRate) * position.amount * multiplier,
  };
};

const STATUS_TEXT = {
  "pending-close": "Selling",
  "close-in-progress": "Selling",
  "automated-close-failed": "Sell failed",
};

const MiniApp = () => {
  const [webApp, setWebApp] = useState(null);
  const [api, setApi] = useState(null);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState(null);
  const [connected, setConnected] = useState(false);
  const [selling, setSelling] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [tab, setTab] = useState("positions");
  const tokenRef = useRef(null);

  // sign in with Telegram's initData -> a Mini App token
  useEffect(() => {
    document.title = "dindicator";
    loadTelegram().then(async (tg) => {
      if (!tg?.initData) {
        setError("Open this page from the dindicator Telegram bot.");
        return;
      }
      tg.ready();
      tg.expand();
      tg.setHeaderColor?.("#111827");
      tg.setBackgroundColor?.("#111827");
      setWebApp(tg);
      try {
        const { data } = await axios.post("/api/miniapp/session", {
          initData: tg.initData,
        });
        tokenRef.current = data.token;
        const instance = axios.create({
          headers: { Authorization: `Bearer ${data.token}` },
          timeout: 15000,
        });
        // an axios instance is a function: React would call it as an updater
        setApi(() => instance);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Couldn't sign in. Link your Telegram in the dashboard settings.",
        );
      }
    });
  }, []);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      const { data } = await api.get("/api/miniapp/positions");
      setPositions(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session ended. Close and reopen the Mini App.");
      }
    }
  }, [api]);

  // full list now, every 30s and when the app comes back to the foreground
  useEffect(() => {
    if (!api) return undefined;
    load();
    const interval = setInterval(load, REFRESH_MS);
    const onVisible = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, load]);

  // live updates of single positions
  useEffect(() => {
    if (!api) return undefined;
    const socket = io({
      path: "/socket",
      auth: { token: tokenRef.current },
      reconnection: true,
    });
    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("position", (position) => {
      setPositions((current) => {
        if (!current) return current;
        const index = current.findIndex((p) => p.tradeId === position.tradeId);
        if (index === -1) {
          load(); // a new position: re-read the list (order, all fields)
          return current;
        }
        const next = [...current];
        next[index] = position;
        return next;
      });
    });
    return () => socket.disconnect();
  }, [api, load]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(interval);
  }, []);

  const totals = useMemo(() => {
    const list = positions || [];
    const value = list.reduce((sum, p) => sum + (p.profit?.value || 0), 0);
    const invested = list.reduce((sum, p) => sum + (p.openValue || 0), 0);
    return {
      value,
      perc: invested > 0 ? (value / invested) * 100 : 0,
    };
  }, [positions]);

  const sell = (position) => {
    const label = tradeLabel(position);
    const confirm = (ok) => {
      if (!ok) return;
      setSelling(position.tradeId);
      api
        .post(`/api/miniapp/positions/${position.tradeId}/sell`)
        .then(() => {
          webApp.HapticFeedback?.notificationOccurred("success");
          webApp.showAlert(`Sell triggered for ${label}. AutoTrader will sell it.`);
        })
        .catch((err) => {
          webApp.HapticFeedback?.notificationOccurred("error");
          webApp.showAlert(err.response?.data?.message || "Sell failed");
        })
        .finally(() => {
          setSelling(null);
          load();
        });
    };
    webApp.showConfirm(`Sell ${position.amount} ${label}?`, confirm);
  };

  if (error) {
    return (
      <div className="mini-app mini-app-message">
        <p>{error}</p>
      </div>
    );
  }
  if (!positions) {
    return (
      <div className="mini-app mini-app-message">
        <Spin />
      </div>
    );
  }

  return (
    <div className="mini-app">
      {tab === "positions" && (
        <>
          <div className="mini-app-header">
            <div>
              <div className="mini-app-title">Live positions</div>
              <div className="mini-app-sub">
                <span className={`mini-app-dot ${connected ? "on" : ""}`} />
                {connected ? "Live" : "Reconnecting…"} · {positions.length} open
              </div>
            </div>
            <div className={`mini-app-total ${returnUpDownClassname(totals.value)}`}>
              <div>{money(totals.value)}</div>
              <div className="mini-app-total-perc">{percent(totals.perc)}</div>
            </div>
          </div>

          {positions.length === 0 && (
            <p className="mini-app-empty">No open positions.</p>
          )}

          {positions.map((position) => (
            <PositionCard
              key={position.tradeId}
              position={position}
              now={now}
              selling={selling === position.tradeId}
              onSell={() => sell(position)}
            />
          ))}
        </>
      )}
      {tab === "history" && <HistoryTab api={api} />}
      {tab === "automation" && <AutomationTab api={api} webApp={webApp} />}

      <nav className="mini-app-tabs">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={tab === key ? "active" : ""}
            onClick={() => {
              webApp?.HapticFeedback?.selectionChanged?.();
              setTab(key);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
};

const PositionCard = ({ position, now, selling, onSell }) => {
  const { option, profit } = position;
  const status = STATUS_TEXT[position.orderStatus];
  // highest / lowest live price since open (AutoTrader's marks)
  const max = pnlAt(position, position.maxLiveRate);
  const min = pnlAt(position, position.minLiveRate);
  const daysLeft = option
    ? dayjs.utc(option.expiration).diff(dayjs.utc(dayjs().format("YYYY-MM-DD")), "day")
    : null;
  const underlyingAway =
    option?.lastUnderlyingPrice && option?.targetUnderlyingPrice
      ? ((option.targetUnderlyingPrice - option.lastUnderlyingPrice) /
          option.lastUnderlyingPrice) *
        100
      : null;

  return (
    <div className="mini-app-card">
      <div className="mini-app-row">
        <div>
          <div className="mini-app-symbol">
            {tradeLabel(position)}
            {status && (
              <Tag color={status === "Sell failed" ? "red" : "orange"}>{status}</Tag>
            )}
          </div>
          <div className="mini-app-sub">{position.strategyName}</div>
        </div>
        <div className={`mini-app-pnl ${returnUpDownClassname(profit?.value)}`}>
          <div className="mini-app-pnl-value">{money(profit?.value)}</div>
          <div className="mini-app-pnl-perc">{percent(profit?.perc)}</div>
        </div>
      </div>

      <div className="mini-app-details">
        <span>
          {position.currentRate ?? "-"} <small>now</small>
        </span>
        <span>
          {position.openRate} <small>open</small>
        </span>
        <span>
          {position.amount} <small>{option ? "contracts" : "shares"}</small>
        </span>
      </div>

      {(max || min) && (
        <div className="mini-app-details">
          <span>
            <small>max</small> {position.maxLiveRate ?? "-"}{" "}
            <small className={returnUpDownClassname(max?.value)}>
              {max ? `${money(max.value)} (${percent(max.perc)})` : ""}
            </small>
          </span>
          <span>
            <small>min</small> {position.minLiveRate ?? "-"}{" "}
            <small className={returnUpDownClassname(min?.value)}>
              {min ? `${money(min.value)} (${percent(min.perc)})` : ""}
            </small>
          </span>
        </div>
      )}

      {option && (
        <div className="mini-app-details">
          <span>
            {option.lastUnderlyingPrice ?? "-"} <small>underlying</small>
          </span>
          <span>
            {option.targetUnderlyingPrice ?? "-"} <small>target</small>
            {underlyingAway !== null && (
              <small>
                {" "}
                ({underlyingAway > 0 ? "+" : ""}
                {underlyingAway.toFixed(2)}%)
              </small>
            )}
          </span>
          <span>
            {daysLeft} <small>days left</small>
          </span>
        </div>
      )}

      <div className="mini-app-row mini-app-footer">
        <span className="mini-app-sub">{ago(position.lastLiveRateUpdate, now, "no live price yet")}</span>
        {position.canSell && (
          <Button danger size="small" loading={selling} onClick={onSell}>
            Trigger sell
          </Button>
        )}
      </div>
    </div>
  );
};

export default MiniApp;
